import { MouseEvent, FunctionComponent, useState, useEffect } from "react"
import { v4 as uuidv4 } from "uuid"
import { filteredUsersType } from "../features/simulation/types"
import TableSearch from "./table-search"
import TablePagination from "./table-pagination"
import TableHead, { FilterConfig } from "./table-head"
import TableTop from "./table-top"
import TableBody from "./table-body"
import { TableButtonProps } from "./table-button"
import TableMetaData from "./table-metadata"
import { Bar } from "react-chartjs-2"
import ChartAnnotation from "chartjs-plugin-annotation"
import {
  ChartConfiguration,
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js"
import { RootState, useTypedSelector } from "../app/store"
import { RecruiterPortal } from "../utils/constants"
import { useLocation } from "react-router-dom"
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartAnnotation,
)
type TableProps = {
  columns: Column[]
  data: DataColumn[][]
  buttons?: TableButtonProps[]
  rowClickAction?: (e: MouseEvent<HTMLTableRowElement>) => void
  onSelectionChange?: (
    selectedRows: {
      check: boolean
      index: number
      usersDetails: DataColumn[]
    }[],
  ) => void
  onVisibleRowsChange?: (
    visibleRows: {
      check: boolean
      index: number
      usersDetails: DataColumn[]
    }[],
  ) => void
  showCheckboxColumn?: boolean
  disablePagination?: boolean
  filterConfig?: { [key: string]: FilterConfig }
  loading?: boolean
  setSearchText?: (text: string) => void
  clearSearch?: boolean
  setClearSearch?: (text: boolean) => void
  metaData?: { label: string; value: string | number }[]
  newData?: {
    check: boolean
    index: number
    usersDetails: DataColumn[]
    usersDetailsRaw?: filteredUsersType
  }[]
  graphData?: {
    id: number
    graph: string
    data: ChartConfiguration<"bar">["data"]
    options: ChartConfiguration<"bar">["options"]
  }[]
  activeGraph?: string
  graphPlaceHolder?: {
    compositeSetEnabled: boolean
    selectMeasureMessage: boolean
  }
  updateDataByGroups?: (
    groupsSelected: string[],
    state?: string,
    type?: string,
  ) => void
  showSearch?: boolean
  addPadding?: boolean
  onCurrentRowsChange?: (
    currentRows: {
      check: boolean
      index: number
      usersDetails: DataColumn[]
      usersDetailsRaw?: filteredUsersType
    }[],
  ) => void
  breadcrumbsDisplay?: {
    label: string
    action: () => void
    display: boolean
    key: number
  }[]
  handleSearchUpdateWithGraph?: (
    results: filteredUsersType[],
    tabShift?: boolean,
  ) => void
}

export type Column = {
  name: string
  styles?: string
  iconDataURL?: string
  value?: string | JSX.Element
  label?: string
}

export type DataColumn = {
  id: string
  name: string
  value: string | JSX.Element
}

export const Table: FunctionComponent<TableProps> = ({
  columns,
  data,
  buttons,
  rowClickAction,
  onSelectionChange,
  onVisibleRowsChange,
  showCheckboxColumn = true,
  disablePagination = false,
  filterConfig,
  loading = false,
  setSearchText,
  clearSearch,
  setClearSearch,
  metaData,
  newData,
  graphData,
  activeGraph,
  graphPlaceHolder,
  updateDataByGroups,
  showSearch = true,
  addPadding = true,
  onCurrentRowsChange,
  breadcrumbsDisplay,
  handleSearchUpdateWithGraph,
}) => {
  const location = useLocation()
  const authStore = useTypedSelector((state: RootState) => {
    return state.authSlice
  })
  const isRecruiter = authStore.user?.roles?.[0]?.name === "Recruiter"
  const [chartKey, setChartKey] = useState(uuidv4())

  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set())
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(
    null,
  )
  const [filteredData, setFilteredData] = useState<
    {
      check: boolean
      index: number
      usersDetails: DataColumn[]
      usersDetailsRaw?: filteredUsersType
    }[]
  >([])

  // Store the original data
  const [originalData, setOriginalData] = useState<
    {
      check: boolean
      index: number
      usersDetails: DataColumn[]
      usersDetailsRaw?: filteredUsersType
    }[]
  >([])
  const [paginationState, setPaginationState] = useState({
    currentPage: 1,
    rowsPerPage: 5,
  })

  // New state for sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "off">(
    "off",
  )

  // New state for filtering
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({})
  const [allFilters, setAllFilters] = useState<{
    selectedFilters: { [key: string]: string[] }
    confirmedFilters: { [key: string]: string[] }
  }>({ selectedFilters: {}, confirmedFilters: {} })

  // Calculate the current rows to display based on pagination
  const indexOfLastRow =
    paginationState.currentPage * paginationState.rowsPerPage
  const indexOfFirstRow = indexOfLastRow - paginationState.rowsPerPage
  const currentRows = disablePagination
    ? filteredData
    : filteredData.slice(indexOfFirstRow, indexOfLastRow)

  // Sort data based on the selected column and direction
  const sortData = () => {
    if (sortDirection === "off") {
      return originalData // Return original data when sort is off
    }
    const sortColumnName =
      sortColumn == "Name" || sortColumn == "Email"
        ? sortColumn?.toLowerCase()
        : sortColumn
    const returnValue = [...filteredData].sort((a, b) => {
      const aValue =
        a.usersDetails.find((col) => {
          return col.name === sortColumnName
        })?.value || ""
      const bValue =
        b.usersDetails.find((col) => col.name === sortColumnName)?.value || ""

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1
      return 0
    })
    return returnValue
  }
  // Initialize filteredData and originalData when data changes
  useEffect(() => {
    setFilteredData(newData || [])
    setOriginalData(newData || [])
  }, [newData])

  // Update visible rows when pagination changes
  useEffect(() => {
    // Only update visible rows if pagination is enabled
    if (!disablePagination) {
      onVisibleRowsChange?.(currentRows)
    } else {
      onVisibleRowsChange?.(filteredData) // Send all rows if pagination is disabled
    }
  }, [
    paginationState.currentPage,
    paginationState.rowsPerPage,
    filteredData,
    disablePagination,
  ])

  useEffect(() => {
    const sortedData = sortData()
    setFilteredData(sortedData)
  }, [sortColumn, sortDirection])

  useEffect(() => {
    onVisibleRowsChange?.(currentRows)

    setChartKey(uuidv4())
  }, [graphData])
  useEffect(() => {
    onVisibleRowsChange?.(currentRows)
  }, [])

  useEffect(() => {
    if (graphData && graphData.length > 0) {
      setAllFilters((prevFilters) => ({
        ...prevFilters,
        selectedFilters: {},
        confirmedFilters: {},
      }))
    }
  }, [location.pathname])

  const handleSort = (columnName: string) => {
    if (
      ["activated", "simulation", "groupName", "roleName"].includes(columnName)
    ) {
      return
    }
    if (sortColumn === columnName) {
      // Toggle sort direction
      setSortDirection((prev) =>
        prev === "asc" ? "desc" : prev === "desc" ? "off" : "asc",
      )

      if (sortDirection === "off") {
        setSortColumn(null)
      }
    } else {
      // Set new sort column and default to ascending
      setOriginalData(filteredData) // Store the data as is prior to sorting
      setSortColumn(columnName)
      setSortDirection("asc")
    }
  }

  const handleSelectAll = () => {
    const currentRowsIndexes = currentRows.map((item) => item.index)
    const allSelected = currentRowsIndexes.every((index) => {
      if (selectedIndexes.has(index)) {
        return true
      }
      return false
    })
    if (allSelected) {
      const visibleRowsSet = new Set<number>()

      filteredData.forEach((item) => {
        if (currentRowsIndexes.includes(item.index)) {
          item.check = false
          visibleRowsSet.add(item.index)
        }
      })

      const newSet = new Set(selectedIndexes)
      visibleRowsSet.forEach((index) => newSet.delete(index))
      onSelectionChange?.(Array.from(newSet).map((i) => filteredData[i]))
      setSelectedIndexes(newSet)
      // Notify upstream components of the selected rows
    } else {
      const visibleRowsSet = new Set<number>()
      filteredData.forEach((item) => {
        if (currentRowsIndexes.includes(item.index)) {
          item.check = true
          visibleRowsSet.add(item.index)
        }
      })

      const newSet = new Set(selectedIndexes)
      visibleRowsSet.forEach((index) => newSet.add(index))
      onSelectionChange?.(Array.from(newSet).map((i) => filteredData[i]))
      setSelectedIndexes(newSet)
      // Notify upstream components of the selected rows
    }
    // Call onVisibleRowsChange only if the visible rows change
    if (filteredData.length !== data.length) {
      onVisibleRowsChange?.(filteredData)
    }
  }

  const updateSelectedRows = () => {
    filteredData.forEach((item) => {
      item.check = false
    })
    setSelectedIndexes(new Set())
    onSelectionChange?.([])
  }

  const handleRowSelect = (index: number, shiftKey: boolean) => {
    const newSelectedIndexes = new Set(selectedIndexes)

    if (shiftKey && lastSelectedIndex !== null) {
      const start = Math.min(lastSelectedIndex, index)
      const end = Math.max(lastSelectedIndex, index)
      for (let i = start; i <= end; i++) {
        newSelectedIndexes.add(i)
      }
    } else {
      const currentItemSelected = Array.from([index]).map((i) => currentRows[i])
      currentItemSelected.forEach((item) => {
        if (newSelectedIndexes.has(item.index)) {
          newSelectedIndexes.delete(item.index)
        } else {
          newSelectedIndexes.add(item.index)
        }
      })
      setLastSelectedIndex(index)
    }
    filteredData.forEach((item) => {
      item.check = newSelectedIndexes.has(item.index)
    })
    setSelectedIndexes(newSelectedIndexes)
    onSelectionChange?.(
      Array.from(newSelectedIndexes).map((i) => filteredData[i]),
    )
  }

  const handleSearchResults = (
    results: {
      check: boolean
      index: number
      usersDetails: DataColumn[]
      usersDetailsRaw?: filteredUsersType
    }[],
    queryToSearch?: string,
    tabShift?: boolean,
  ) => {
    if (tabShift) {
      handleSearchUpdateWithGraph?.(
        results.map(
          (item) => item.usersDetailsRaw || ({} as filteredUsersType),
        ),
        true,
      )
    }
    if (queryToSearch && isRecruiter) {
      if (queryToSearch.length > 2) {
        setPaginationState((prev) => ({ ...prev, currentPage: 1 }))
      }

      handleSearchUpdateWithGraph?.(
        results.map(
          (item) => item.usersDetailsRaw || ({} as filteredUsersType),
        ),
      )
    }
    setFilteredData(results)

    // Clear selected rows if no results are found
    if (results.length === 0) {
      setSelectedIndexes(new Set())
      onSelectionChange?.([]) // Notify upstream components of the cleared selection
    } else {
      setSelectedIndexes(new Set())
    }

    // Call onVisibleRowsChange with the current filtered data
    onVisibleRowsChange?.(results)
  }

  const handleFilterChange = (columnNameRaw: string, value: string) => {
    const columnName = columnNameRaw

    // For recruiters with updateDataByGroups callback, groupName uses the Apply button flow
    // Otherwise, all filters work immediately (including groupName)
    const isGroupNameFilter = columnName === "groupName"
    const useApplyButtonFlow = isRecruiter && isGroupNameFilter && updateDataByGroups

    if (useApplyButtonFlow) {
      // Recruiter logic for groupName filter when updateDataByGroups is provided (uses Apply button)
      if (value === "Apply") {
        const type = breadcrumbsDisplay?.[2]?.display
          ? "integrated"
          : breadcrumbsDisplay?.[1]?.label

        updateDataByGroups(
          allFilters.selectedFilters.groupName,
          "apply", // state
          type, //type
        )
        setAllFilters((prevFilter) => ({
          ...prevFilter,
          confirmedFilters: prevFilter.selectedFilters,
        }))
        return
      } else if (value === "clear_selected") {
        const type = breadcrumbsDisplay?.[2]?.display
          ? "integrated"
          : breadcrumbsDisplay?.[1]?.label
        updateDataByGroups(
          allFilters.selectedFilters.groupName,
          "clear", // state
          type, //type
        )
        const newFilters = { ...allFilters.selectedFilters }
        delete newFilters[columnName]
        setAllFilters((prevFilter) => ({
          ...prevFilter,
          selectedFilters: newFilters,
          confirmedFilters: newFilters,
        }))
        return
      } else if (value === "closed") {
        setAllFilters((prevFilter) => ({
          ...prevFilter,
          selectedFilters: prevFilter.confirmedFilters,
        }))
      } else {
        setAllFilters((prevFilter) => ({
          ...prevFilter,
          selectedFilters: filterFunctionality(
            prevFilter.selectedFilters,
            columnName,
            value,
          ),
        }))
      }
    } else {
      // Standard immediate filtering for all other cases
      if (value === "clear_selected") {
        const newFilters = { ...filters }
        delete newFilters[columnName]
        setFilters(newFilters)
        // Also update allFilters for recruiter UI consistency
        if (isRecruiter) {
          setAllFilters((prevFilter) => ({
            ...prevFilter,
            selectedFilters: newFilters,
            confirmedFilters: newFilters,
          }))
        }
        return
      }
      if (value === "closed") {
        return
      }
      if (value === "Apply") {
        // Apply button just confirms the selection for immediate filters
        return
      }
      const newFilters = filterFunctionality(filters, columnName, value)
      setFilters(newFilters)
      // Also update allFilters for recruiter UI consistency (shows filled filter icon)
      if (isRecruiter) {
        setAllFilters((prevFilter) => ({
          ...prevFilter,
          selectedFilters: newFilters,
          confirmedFilters: newFilters,
        }))
      }
    }
  }

  const filterFunctionality = (
    prevFilters: { [key: string]: string[] },
    columnName: string,
    value: string,
  ) => {
    const newFilters = { ...prevFilters }
    if (newFilters[columnName]) {
      if (newFilters[columnName].includes(value)) {
        newFilters[columnName] = newFilters[columnName].filter(
          (v) => v !== value,
        )
      } else {
        newFilters[columnName] = [...newFilters[columnName], value]
      }

      if (newFilters[columnName].length === 0) {
        delete newFilters[columnName]
      }
    } else {
      newFilters[columnName] = [value]
    }
    return newFilters
  }

  const applyFilters = (
    data: {
      check: boolean
      index: number
      usersDetails: DataColumn[]
      usersDetailsRaw?: filteredUsersType
    }[],
  ) => {
    return data.filter((row) => {
      return Object.entries(filters).every(([columnName, filterValues]) => {
        const columnValue = row.usersDetails.find(
          (col) => col.name === columnName, //groupName === Group
        )?.value
        if (typeof columnValue === "string") {
          return filterValues.some((value) => columnValue.includes(value))
        }
        return true
      })
    })
  }

  useEffect(() => {
    const filtered = applyFilters(newData || [])
    setFilteredData(filtered)
    setPaginationState({ ...paginationState, currentPage: 1 }) // Reset to first page when filters change
    setSelectedIndexes(new Set())
    onSelectionChange?.([]) // Notify upstream components of the cleared selection
  }, [filters])

  useEffect(() => {
    setSelectedIndexes(new Set())
  }, [loading])

  const handleButtonClick = (buttonLabel: string) => {
    if (onCurrentRowsChange && buttonLabel.includes("Export Users")) {
      onCurrentRowsChange(currentRows)
    }
  }
  return (
    <section
      className={
        addPadding
          ? "py-10 px-8 lg:py-16"
          : (isRecruiter ? "py-4 px-8" : "")
      }>
      <div className={isRecruiter ? "" : "mx-auto lg:container"}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Breadcrumbs - hidden for recruiter pages since headers are in dashboard wrapper */}
          {!location.pathname.includes("recruiter") && breadcrumbsDisplay && breadcrumbsDisplay.length > 0 && (
            <div className="flex items-center gap-4 justify-start px-6 py-4 border-b border-gray-100">
              <div className="breadcrumbs">
                <ul>
                  {breadcrumbsDisplay?.map(
                    (crumb, index) =>
                      crumb.display && (
                        <li key={index}>
                          <div className="rounded-md bg-gray-50 flex flex-row py-1 px-2 items-center justify-center text-gray-700">
                            <div className="relative leading-[28px] font-medium text-lg no-underline">
                              <button
                                onClick={
                                  activeGraph ===
                                  RecruiterPortal.OverallPerformance
                                    ? () => {}
                                    : () => {
                                        crumb.action()
                                        if (crumb.key === 1) {
                                          setFilters({})
                                          setAllFilters((prevFilters) => ({
                                            ...prevFilters,
                                            selectedFilters: {},
                                            confirmedFilters: {},
                                          }))
                                        }
                                      }
                                  // need to clear filter icon when breadcrumb is clicked
                                }
                                className="text-gray-700 bg-transparent hover:underline">
                                {crumb.label}
                              </button>
                            </div>
                          </div>
                        </li>
                      ),
                  )}
                </ul>
              </div>
            </div>
          )}
          <div className="flex items-center gap-4 justify-between px-6 py-4 border-b border-gray-100">
            <TableTop
              buttons={buttons?.map((button) => ({
                ...button,
                action: (event: React.MouseEvent<HTMLButtonElement>) => {
                  button.action(event)
                  handleButtonClick(button.label)
                },
              }))}
            />
            {showSearch ? (
              <TableSearch
                data={newData || []}
                columns={columns}
                onSearchResults={handleSearchResults}
                setSearchText={setSearchText || (() => {})}
                clearSearch={clearSearch ?? false}
                setClearSearch={setClearSearch || (() => {})}
              />
            ) : null}
          </div>
          {metaData && metaData.length > 0 && (
            <div className="flex items-center">
              <TableMetaData metaData={metaData} />
            </div>
          )}

          {graphData && graphPlaceHolder?.compositeSetEnabled && (
            <div
              className="py-2 lg:py-2"
              style={{ height: "32vh", width: "100%" }}>
              <Bar
                data-testid={`${activeGraph}_${chartKey}_graph`}
                // key={graphData?.find((item) => item.graph === activeGraph)?.id}
                key={chartKey}
                data={
                  graphData?.find((item) => item.graph === activeGraph)
                    ?.data as ChartConfiguration<"bar">["data"]
                }
                options={
                  graphData?.find((item) => item.graph === activeGraph)?.options
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                plugins={[ChartAnnotation as any]}
              />
            </div>
          )}
          {graphPlaceHolder &&
          (graphPlaceHolder?.selectMeasureMessage ||
            !graphPlaceHolder?.compositeSetEnabled) ? (
            <div
              className="py-2 lg:py-2 flex justify-center items-center"
              style={{ height: "30vh", width: "100%" }}>
              <p className="text-center text-sm text-gray-500">
                {graphPlaceHolder?.selectMeasureMessage
                  ? "Select a measure to view the graph"
                  : "No composite score sets available"}
              </p>
            </div>
          ) : null}

          <div className="py-4 px-6 overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <TableHead
                columns={columns}
                allSelected={currentRows.every((item) => {
                  if (selectedIndexes.has(item.index)) {
                    return true
                  }
                  return false
                })}
                onSelectAll={handleSelectAll}
                showCheckboxColumn={showCheckboxColumn}
                onSort={handleSort} // Pass the sort handler
                sortColumn={sortColumn} // Pass the current sort column
                sortDirection={sortDirection} // Pass the current sort direction
                filterConfig={filterConfig} // Pass the filter config
                filters={isRecruiter ? allFilters.selectedFilters : filters}
                handleFilterChange={handleFilterChange}
              />
              <TableBody
                data={currentRows} // Use currentRows for pagination
                rowClickAction={rowClickAction}
                onRowSelect={handleRowSelect}
                showCheckboxColumn={showCheckboxColumn}
                columns={columns}
                loading={loading}
              />
            </table>
          </div>

          {/* Conditionally render pagination controls */}
          {!disablePagination && (
            <div className="flex items-center justify-end gap-4 px-6 py-4 border-t border-gray-100 text-sm">
              <div className="flex items-center gap-2">
                <label htmlFor="rowsPerPage" className="text-gray-500">
                  Rows per page:
                </label>
                <select
                  id="rowsPerPage"
                  value={paginationState.rowsPerPage}
                  onChange={(e) => {
                    setPaginationState({
                      ...paginationState,
                      rowsPerPage: Number(e.target.value),
                      currentPage: 1,
                    }) // Reset to first page when changing rows per page
                    updateSelectedRows()
                  }}
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <TablePagination
                totalRows={filteredData.length}
                rowsPerPage={paginationState.rowsPerPage}
                currentPage={paginationState.currentPage}
                onPageChange={(page) => {
                  setPaginationState({
                    ...paginationState,
                    currentPage: page,
                  })
                  updateSelectedRows()
                }}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default Table
